import { FlagOutlined, ReloadOutlined, SaveOutlined, WarningOutlined, SafetyOutlined, HistoryOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, message, Modal, Row, Select, Spin, Tooltip } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { formatDisplayDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import BooleanTag from '@/components/app/BooleanTag';
import { GenderType } from '@/constants';
import CountryInput from '@/components/FormItems/CountryInput';
import AppDatePicker from '@/components/FormItems/DatePicker';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import ExportBtn from '@/components/app/ExportBtn';
import { exportService } from '@/services/exportService';
import { getFullName } from '@/helpers/string';
import RegionInput from '@/components/FormItems/RegionInput';
import CityInput from '@/components/FormItems/CityInput';
import UpdatePassengerIdentityModal from './components/updatePassengerIdentityModal';
import PassengerHistoryModal from './components/PassengerHistoryModal';
import WithoutPhoneCountsModal from './components/WithoutPhoneCountsModal';
import InvalidPhonesModal from './components/InvalidPhonesModal';
import PassengerUpdateNotice from '@/components/app/PassengerUpdateNotice';
import { PassengerDetailModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import PhoneCodeInput from '@/components/FormItems/PhoneCodeInput';
import useHasPermissions from '@/hooks/useHasPermissions';
import { APP_PERMISSIONS } from '@/services/roleService';

const PAGE_TITLE = 'passengersPage_Title';

const columns: IColumnType<PassengerDetailModel>[] = [
  {
    title: 'base.FullName',
    dataIndex: 'id',
    ellipsis: true,
    sorter: true,
    defaultSortOrder: 'ascend',
    render: (v, record) => getFullName(record.firstName, record.lastName),
    width: 200,
  },
  {
    title: 'base.Email',
    dataIndex: 'emailAddress',
    sorter: true,
    ellipsis: true,
    width: 200,
  },
  {
    title: 'base.Country',
    dataIndex: 'countryName',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'base.PhoneNumber',
    dataIndex: 'phoneNumber',
    render: (_, record) => [record.phoneCode, record.phoneNumber].join(' '),
    ellipsis: true,
    width: 150,
  },
  {
    title: 'base.DateOfBirth',
    dataIndex: 'dateOfBirth',
    width: 120,
    ellipsis: true,
    render: v => (v ? formatDisplayDate(v) : ''),
    sorter: true,
  },
  {
    title: 'passengersPage_PassengerType',
    dataIndex: 'passengerType',
    ellipsis: true,
    width: 120,
  },
  {
    title: 'base.Gender',
    dataIndex: 'gender',
    width: 100,
    ellipsis: true,
    render: v => GenderType.__getValue(v),
    sorter: true,
  },

  {
    title: 'passengersPage_IsTicketBookingAllowed',
    dataIndex: 'isTicketBookingAllowed',
    sorter: true,
    width: 120,
    align: 'center',
    render: v => <BooleanTag value={v} />,
    ellipsis: true,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'searchText', label: 'passengersPage_SearchText', mdSize: 8 },
  { type: 'SELECT', name: 'countryId', label: 'base.Country', mdSize: 8, customElm: <CountryInput /> },
];

const PassengersPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [updateTicketPassengerIdentityId, setUpdateTicketPassengerIdentityId] = React.useState<string>();
  const [historyPassengerId, setHistoryPassengerId] = React.useState<string>();
  const [showWithoutPhone, setShowWithoutPhone] = React.useState(false);
  const [showInvalidPhones, setShowInvalidPhones] = React.useState(false);
  const canUpdatePassenger = useHasPermissions([APP_PERMISSIONS.ManagePassenger]);
  React.useEffect(() => {
    apiSdk.BookingPassengerService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);

  const updateEntity = values => {
    setLoading(true);
    apiSdk.BookingPassengerService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportPassengers({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportPassengers({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toPdf();
  };

  const changeBookingAllow = (passengerId: string, isBlock: boolean) => {
    let api = isBlock ? apiSdk.BookingPassengerService.block({ id: passengerId }) : apiSdk.BookingPassengerService.unblock({ id: passengerId });
    setLoading(true);
    api.catch(message.error).finally(() => {
      setLoading(false);
      zTb.current?.reload();
    });
  };

  const editForm = (passengerId: string) => {
    setLoading(true);
    apiSdk.BookingPassengerService.getPassengerDetails({ id: passengerId })
      .then(res => {
        setIsNewForm(false);
        setFormVisible(true);
        form.setFieldsValue(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <Button key="invalid-phones" icon={<WarningOutlined />} data-testid="invalid-phones-open" onClick={() => setShowInvalidPhones(true)}>
          {t('invalidPhones_Button')}
        </Button>,
        <Button key="without-phone" icon={<PhoneOutlined />} data-testid="without-phone-open" onClick={() => setShowWithoutPhone(true)}>
          {t('withoutPhone_Button')}
        </Button>,
        <ExportBtn
          disabled={!exportColumns?.length}
          onClick={() => {
            toggleExportModal(true);
          }}
        />,
      ]}
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<PassengerDetailModel>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/bookings/booking-passengers/get-passengers"
          bordered
          order_by="firstName"
          order_by_asc={true}
          allowEdit={canUpdatePassenger}
          onEdit={record => {
            editForm(record.id || '');
          }}
          extraActions={[
            {
              render: record => (
                <Tooltip title={t('passengerHistory_Title')}>
                  <Button size="small" data-testid={`passenger-history-${record.id}`} onClick={() => setHistoryPassengerId(record.id)} icon={<HistoryOutlined />} />
                </Tooltip>
              ),
            },
            ...(canUpdatePassenger
              ? [
                  {
                    render: record => {
                      return (
                        <Tooltip title={t('passengersPage_IsTicketBookingAllowed')}>
                          <Button
                            size="small"
                            onClick={() => {
                              changeBookingAllow(record.id || '', !!record.isTicketBookingAllowed);
                            }}
                            type="primary"
                            danger={record.isTicketBookingAllowed}
                            icon={record.isTicketBookingAllowed ? <WarningOutlined /> : <ReloadOutlined />}
                          />
                        </Tooltip>
                      );
                    },
                  },
                  {
                    render: record => {
                      return (
                        <Tooltip title={t('manageTicket_UpdatePassengerIdentity')}>
                          <Button
                            size="small"
                            onClick={() => {
                              setUpdateTicketPassengerIdentityId(record.id);
                            }}
                            type="default"
                            icon={<SafetyOutlined />}
                          />
                        </Tooltip>
                      );
                    },
                  },
                ]
              : []),
          ]}
          allowDelete={canUpdatePassenger}
          filters={filters}
          searchBtnAlignLeft
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            style={{ top: 10 }}
            className="nta-custom-form"
            title={isNewForm ? t('passengersPage_CreatePassenger') : t('passengersPage_UpdatePassenger')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              {!isNewForm ? <PassengerUpdateNotice /> : null}
              <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={ROW_GUTTER}>
                  <Col md={24}>
                    <Form.Item label={t('base.FirstName')} name="firstName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('base.LastName')} name="lastName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('base.DateOfBirth')} name="dateOfBirth" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <AppDatePicker className="w-100" />
                    </Form.Item>
                    <Form.Item label={t('base.PhoneCode')} name="phoneCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <PhoneCodeInput />
                    </Form.Item>
                    <Form.Item label={t('base.PhoneNumber')} name="phoneNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('base.Email')} name="emailAddress">
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('base.Country')} name="countryId">
                      <CountryInput
                        isOperatingOnly={false}
                        onChange={() => {
                          form.setFieldValue('regionId', null);
                        }}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.Region')} name="regionId">
                      <RegionInput
                        disabled={!countryId}
                        countryId={countryId}
                        onChange={() => {
                          form.setFieldValue('cityId', null);
                        }}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.City')} name="cityId">
                      <CityInput regionId={regionId} disabled={!regionId} />
                    </Form.Item>

                    <Form.Item label={t('base.Gender')} name="gender" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Select options={GenderType.__getSelectOptions()} />
                    </Form.Item>
                    <Form.Item label=" " colon={false} valuePropName="checked" name="isTicketBookingAllowed">
                      <Checkbox>{t('passengersPage_IsTicketBookingAllowed')}</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col md={12}></Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {showExportModal ? (
        <ExportColumnsSelectorModal
          title={t('passengerPage_ExportPassengers')}
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
      {historyPassengerId ? <PassengerHistoryModal passengerId={historyPassengerId} onClose={() => setHistoryPassengerId(undefined)} /> : null}
      {showWithoutPhone ? <WithoutPhoneCountsModal onClose={() => setShowWithoutPhone(false)} /> : null}
      {showInvalidPhones ? (
        <InvalidPhonesModal canCorrect={canUpdatePassenger} onClose={() => setShowInvalidPhones(false)} onCorrected={() => zTb.current?.reload()} />
      ) : null}
      {updateTicketPassengerIdentityId ? (
        <UpdatePassengerIdentityModal
          passengerId={updateTicketPassengerIdentityId}
          onClose={(reload: boolean) => {
            setUpdateTicketPassengerIdentityId(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
    </MainContent>
  );
};
export default PassengersPage;
